/* jshint esversion: 6 */
//
// Keeps the Google Analytics field holding a bare GA4 measurement id.
//
// Markup contract:
//   div data-controller="ga-measurement-id" data-ga-measurement-id-message-value="..."
//     input data-ga-measurement-id-target="input"
//           data-action="paste->ga-measurement-id#paste blur->ga-measurement-id#validate"
//     button data-action="click->ga-measurement-id#pasteFromClipboard"
import { Controller } from 'stimulus';

// Same shape Event validates server-side; this one only decides what to show
// before the form is submitted.
const MEASUREMENT_ID = /^G-[A-Z0-9]{4,}$/;
const MEASUREMENT_ID_ANYWHERE = /G-[A-Z0-9]{4,}/i;
const WRAPPER_ERROR_CLASS = 'field_with_errors';
// error--placed: the input has no bottom margin, so the default -10px pull-up
// of .error would sit the message on top of it.
const ERROR_CLASS = 'error error--placed';

export default class extends Controller {
  static targets = ['input', 'button'];

  static values = { message: String };

  connect() {
    if (!this.hasButtonTarget) return;
    // The button is a sibling of the input, so it is anchored to the input's
    // measured box (same reason as password_visibility_controller).
    this._positionButton();
    this._resizeObserver = new window.ResizeObserver(() => this._positionButton());
    this._resizeObserver.observe(this.inputTarget);
  }

  disconnect() {
    if (this._resizeObserver) this._resizeObserver.disconnect();
  }

  // Clipboard read needs a secure context and, on Safari, a per-click
  // permission popup; when it is refused the field just gets focus so a
  // regular Cmd+V goes through the paste handler below.
  pasteFromClipboard() {
    const { clipboard } = window.navigator;
    if (!clipboard || !clipboard.readText) {
      this.inputTarget.focus();
      return;
    }
    clipboard.readText()
      .then((text) => {
        if (!this._apply(text)) this.inputTarget.focus();
      })
      .catch(() => this.inputTarget.focus());
  }

  // Organizers paste the whole gtag snippet or the gtag.js URL from the GA
  // admin; the id is the only part worth keeping.
  paste(event) {
    const clipboard = event.clipboardData || window.clipboardData;
    const pasted = clipboard ? clipboard.getData('text') : '';
    if (this._apply(pasted)) event.preventDefault();
  }

  _apply(text) {
    const match = (text || '').match(MEASUREMENT_ID_ANYWHERE);
    if (!match) return false;

    this.inputTarget.value = match[0].toUpperCase();
    this.validate();
    return true;
  }

  validate() {
    const value = this.inputTarget.value.trim().toUpperCase();
    this.inputTarget.value = value;
    if (value === '' || MEASUREMENT_ID.test(value)) {
      this._clearError();
    } else {
      this._showError();
    }
  }

  // Mirrors the markup simple_form emits for a failed field, so the design
  // system styles it without new CSS.
  _showError() {
    this.element.classList.add(WRAPPER_ERROR_CLASS);
    if (this._errorElement()) return;
    const error = document.createElement('div');
    error.className = ERROR_CLASS;
    error.setAttribute('data-ga-measurement-id-error', '');
    error.textContent = this.messageValue;
    const field = this.inputTarget.closest('.ga-measurement-id__field') || this.inputTarget;
    field.insertAdjacentElement('afterend', error);
  }

  _clearError() {
    this.element.classList.remove(WRAPPER_ERROR_CLASS);
    const error = this._errorElement();
    if (error) error.remove();
  }

  _errorElement() {
    return this.element.querySelector('[data-ga-measurement-id-error]');
  }
}
